/**
 * Inbox Store — email conversations with polling
 */

import { create } from 'zustand';
import { actieBronId, actueleKlantbronId } from '../lib/inbox/actie-bron';
import { kiesActueleKlantbronBasis } from '../lib/inbox/effectieve-klantbron';
import { leesActies, type VoorgesteldeActie, type MailVerbModi, type AfwijsRedenCode } from '../lib/mail-acties';
import { wijzigKlachtBehandeling } from '../lib/klachten';
import type { KlachtBeoordeling, KlachtBehandeling } from '../lib/klacht-plan';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './auth-store';
import { useInboxDrawerStore } from './inbox-drawer-store';
import { leesVraagplan, type MailVraagplan } from '../lib/mail-vraagplan';
import { facturenUitActies, factuurDocumentSleutel, MAX_MAIL_BIJLAGEN_BYTES, MAX_MAIL_BIJLAGEN_LABEL } from '../lib/factuurconcept';
import { offertesUitActies, offerteDocumentSleutel } from '../lib/offerteconcept';
import { AFDOENING_VERB, leesMailAfdoening, leesMailAfdoeningHerstel } from '../lib/mail-afdoening';
import { isKlantBeantwoordNa } from '../lib/inbox/beantwoord-na';

/** Statussen die samen het onafgehandelde werk vormen (tab "Open", tab "Mijn"). */
export const OPEN_STATUSSEN: ReadonlyArray<string> = ['nieuw', 'in_behandeling'];

/**
 * Past een gesprek met `status` nog in het actieve statusfilter? Eén plek voor
 * alle optimistic list-updates, zodat een virtuele tab ('open', 'mijn') niet per
 * call-site opnieuw hoeft te worden uitgeschreven.
 */
export function statusPastBijFilter(filter: string, status: string): boolean {
  if (filter === 'all') return true;
  if (filter === 'open' || filter === 'mijn') return OPEN_STATUSSEN.includes(status);
  if (filter === 'later') return true; // snooze is een eigen filter, geen status
  return filter === status;
}

// ─── TYPES ──────────────────────────────────────────────────────

export interface Conversation {
  id: string;
  gmail_thread_id: string | null;
  mailbox: string;
  brand: string;
  /** Baan (migratie 235): klant | systeem | leverancier | nieuwsbrief | intern | bounce | auto_antwoord | onbekend; null = legacy (klant). */
  baan?: string | null;
  baan_bron?: string | null;
  /** Gevuld voor een persoonlijke mailbox: alleen deze gebruiker ziet het gesprek. */
  eigenaar_email?: string | null;
  channel: 'email' | 'whatsapp' | 'phone';
  phone: string | null;
  subject: string | null;
  snippet: string | null;
  from_email: string | null;
  from_name: string | null;
  status: 'nieuw' | 'in_behandeling' | 'beantwoord' | 'afgehandeld' | 'bounce';
  assigned_to: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_type: 'account' | 'lead' | null;
  contact_id: string | null;
  contact_name: string | null;
  /** Monotone CAS-revisie van customer_id + customer_type. */
  customer_link_revision?: number;
  last_message_at: string | null;
  message_count: number;
  has_unread: boolean;
  /** Het bronbericht (laatste klantmail) heeft bijlagen; afgeleid in werkstap.ts. */
  has_attachments?: boolean;
  created_at: string;
  updated_at: string;
  /** Set when this conversation is a callback-thread created via CallDispositionForm. */
  callback_due_at?: string | null;
  /** The call that spawned this callback thread, if any. */
  callback_source_call_id?: string | null;
  /** 'callback' = render CallbackPanel i.p.v. ComposerPanel (BizziPhone of phone-callback). NULL/undefined = normal. */
  subtype?: 'normal' | 'callback' | null;
  /** True als laatste inkomende message een AI-draft heeft (van /api/inbox/threads response). UI-indicator. */
  prebaked_draft?: boolean;
  mail_werkstap?: import('../lib/inbox/werkstap').MailWerkstap | null;
  /** Privacyarme, server-afgeleide werklijstmetadata; bevat nooit concept- of berichttekst. */
  inbox_meta?: import('../lib/inbox/werkstap').InboxAfgeleidMeta;
  /**
   * Goedgekeurde AI-partneraanvraag op dit gesprek (mail_partner_runs), zolang
   * de lead open staat. 'voorbereid' = nog niet aan de partner verstuurd; bij
   * een antwoord aan de klant zet reply.ts de partner dan in de cc.
   */
  partner_aanvraag?: { status: string; lead_nummer: string; partner_naam: string; cc_email: string } | null;
  /** Snooze: tot dit moment onzichtbaar in de gewone tabs, zichtbaar in de 'Later'-tab. NULL = niet gesnoozed. */
  snoozed_until?: string | null;
  snoozed_by?: string | null;
  /** Mogelijke klant-matches uit body-parsing (BizziPhone). Max 5. NULL = nog niet geprobeerd. */
  suggested_customer_ids?: SuggestedMatch[] | null;
  /** Tijdstip waarop auto-resolver een koppeling maakte. NULL = handmatig of nog niet gekoppeld. */
  auto_linked_at?: string | null;
  /** manual | strong_phone_and_email | strong_phone | strong_email | suggestion_accepted | manual_corrected. Voedt KPI "% inbox-koppelingen autonoom". */
  auto_link_signal?: string | null;
  /**
   * Echte ontvanger voor antwoorden — bij relay-mails (info@x.nl namens
   * jelmer@…) wijst dit naar Reply-To i.p.v. From. UI gebruikt dit voor pre-fill
   * van het Aan-veld + "Echte afzender"-rij in Klantinfo.
   * NULL voor conversations zonder inkomende messages.
   */
  effective_sender?: {
    email: string;
    name: string;
    /**
     * 'conversation' = de sync heeft de klant uit een relay-body gevist en op
     * de conversation gezet; het adres van de laatste inkomende mail is een
     * doorgeefluik. Zie api/_lib/ontvanger-rangorde.ts.
     */
    source: 'reply-to' | 'conversation' | 'from';
  } | null;
  /**
   * Deze thread is de risico-mail waarin we de klant om een keuze vroegen over
   * een kleed. Het antwoord hoort via /risicos verwerkt te worden, niet als
   * gewone reply — anders blijft het kleed liggen. NULL als er geen risico aan
   * deze thread hangt (verreweg de meeste threads).
   */
  risico?: {
    /** Er wacht nog een besluit; na verwerking staat dit op false. */
    open: boolean;
    wasserijItemIds: string[];
  } | null;
}

/**
 * Eén kandidaat-match uit de auto-resolver. Mirror van CustomerCandidate uit
 * api/_lib/customer-resolver.ts, gestripped van server-only velden.
 */
export interface SuggestedMatch {
  id: string;
  type: 'account' | 'lead';
  name: string;
  email: string | null;
  phone: string | null;
  contactId?: string | null;
  contactName?: string | null;
  signal: import('../lib/inbox/klant-signaal-label').KlantSignaal;
}

export interface Message {
  ai_afdoening_herstel?: import('../lib/mail-afdoening').MailAfdoeningHerstel | null;
  ai_whatsapp_afhandeling?: import('../lib/inbox/whatsapp-afhandeling').WhatsappAfhandeling | null;
  ai_ongedekt?: string[] | null;
  /** Eén zin "wat de klant wil" uit de lezer (V3 toont hem boven het gesprek). */
  ai_samenvatting?: string | null;
  ai_policy_blokkades?: unknown[] | null;
  ai_intent?: string | null;
  ai_klacht_categorie?: string | null;
  inbox_effective_source_type?: string | null;
  id: string;
  conversation_id: string;
  gmail_message_id: string | null;
  channel: 'email' | 'whatsapp' | 'phone';
  external_id: string | null;
  is_incoming: boolean;
  from_email: string;
  from_name: string | null;
  /** Reply-To header email — alleen gevuld als ≠ from_email. NULL voor normale mails. */
  reply_to_email?: string | null;
  reply_to_name?: string | null;
  to_email: string | null;
  cc_email: string | null;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  message_date: string;
  has_attachments: boolean;
  ai_draft_text: string | null;
  ai_draft_status: string | null;
  ai_draft_generated_at?: string | null;
  /** Model dat de draft maakte (provider-experiment, migratie 018). Admin-only in de UI. */
  ai_draft_model?: string | null;
  /** Confidence (0-100) + reden van de draft-generator. Gepersisteerd sinds migratie 015. */
  ai_draft_confidence?: number | null;
  ai_draft_confidence_reason?: string | null;
  /** Intern klacht-advies (JSON-string) — migratie 017. Nooit naar de klant. */
  ai_klacht_advies?: string | null;
  ai_klacht_advies_status?: string | null;
  ai_klacht_advies_generated_at?: string | null;
  /** Voorgestelde agent-acties bij de draft (mail-agent, migratie 156). Display-only in de UI. */
  ai_acties?: VoorgesteldeActie[] | null;
  ai_actie_context?: string | null;
  ai_vraagplan?: MailVraagplan | null;
  /** Uitgaande template-WhatsApp uit wa_send_log (alleen-lezen context in de thread, id `wasend-<id>`; api/_lib/wa-thread-context.ts). */
  wa_template?: { name: string; label: string; status: string; status_label: string } | null;
  /** Transient client-only flag voor optimistic send. 'sending' = API in flight, 'sent' = API succeeded, 'failed' = API rejected (rolled back so usually removed). */
  _optimisticStatus?: 'sending' | 'sent' | 'failed';
}

export interface TeamMember {
  email: string;
  displayName: string;
  role: string;
}

export interface Attachment {
  id: string;
  message_id: string;
  gmail_attachment_id: string;
  filename: string;
  mime_type: string;
  size: number;
}

export interface LeadProduct {
  id: string;
  name: string;
  quantity: number;
  total: number;
}

export interface LeadInfo {
  id: string;
  reference: string;
  name: string;
  status: string;
  redenNietGeintereseerd: string;
  offerteUrl: string;
  total: number;
  createdDate: string;
  products: LeadProduct[];
}

export interface ContactInfo {
  id: string;
  name: string;
  email: string;
  phone: string;
  title: string;
}

export interface CustomerMatch {
  found: boolean;
  customer: {
    id: string;
    type: 'account' | 'lead';
    name: string;
    phone: string;
    email: string;
    city: string;
    // Blacklist (account-niveau). Leads altijd false/null.
    blacklisted?: boolean;
    blacklistReden?: string | null;
    // B2B contact sub-object (Business Account + contact-level match)
    contact?: ContactInfo;
    // Lead-specific fields
    leadStatus?: string;
    leadSource?: string;
    company?: string;
  } | null;
  leads?: LeadInfo[];
  orders: Array<{
    id: string;
    reference: string;
    status: string;
    brand: string;
    total: number;
    created_date: string;
    contact?: { id: string; name: string } | null;
  }>;
}

// ─── HELPERS ────────────────────────────────────────────────────

const AUTO_ADVANCE_KEY = 'inbox-auto-advance';

function readAutoAdvancePref(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.localStorage.getItem(AUTO_ADVANCE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function writeAutoAdvancePref(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTO_ADVANCE_KEY, enabled ? 'true' : 'false');
  } catch { /* localStorage disabled */ }
}

/** Find next conversation in list (after currentId) that's still actionable. Falls back to previous. Returns null if none. */
function findAdjacentThreadId(
  conversations: Conversation[],
  currentId: string,
  direction: 'next' | 'prev',
): string | null {
  const idx = conversations.findIndex(c => c.id === currentId);
  const isOpen = (c: Conversation) => c.status === 'nieuw' || c.status === 'in_behandeling';
  if (idx === -1) {
    // Current conv not in list (e.g. already removed by status filter) — pick first open
    const first = conversations.find(isOpen);
    return first?.id ?? null;
  }
  if (direction === 'next') {
    for (let i = idx + 1; i < conversations.length; i++) {
      const c = conversations[i];
      if (c && isOpen(c)) return c.id;
    }
    // Wrap-fallback to previous open
    for (let i = idx - 1; i >= 0; i--) {
      const c = conversations[i];
      if (c && isOpen(c)) return c.id;
    }
  } else {
    for (let i = idx - 1; i >= 0; i--) {
      const c = conversations[i];
      if (c && isOpen(c)) return c.id;
    }
    for (let i = idx + 1; i < conversations.length; i++) {
      const c = conversations[i];
      if (c && isOpen(c)) return c.id;
    }
  }
  return null;
}

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

class InboxApiFout extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'InboxApiFout';
  }
}

/** Berichten waarvoor V3 de kaarten al één keer opnieuw liet controleren (geen lus bij een blijvende blokkade). */
const herpreviewGedaan = new Set<string>();
/** Berichten waarvoor V3 al één keer zelf de lezer startte bij het openen. */
const autoLeesGedaan = new Set<string>();

async function apiFetch(url: string, options: RequestInit = {}) {
  const token = await getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(url, { ...options, headers: { ...headers, ...options.headers as Record<string, string> } });
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Request failed' })) as { error?: string; code?: string };
    throw new InboxApiFout(err.error || 'Request failed', response.status, err.code);
  }
  return response.json();
}

// ─── STORE ──────────────────────────────────────────────────────

/** Intern klacht-advies (parsed uit messages.ai_klacht_advies). */
export interface KlachtAdvies {
  plan?: import('../lib/klacht-plan').KlachtPlan;
  goedkeuring_nodig?: string[];
  schrijfversie?: string;
  positie: string;
  compensatie: string;
  waarom: string;
  valkuilen: string[];
}

/** Parse ai_klacht_advies (JSON-string) → KlachtAdvies, of null bij fout/leeg. */
function parseKlachtAdvies(raw: string | null | undefined): KlachtAdvies | null {
  if (!raw) return null;
  try {
    const a = JSON.parse(raw);
    if (!a || typeof a !== 'object') return null;
    return {
      ...(a.plan?.generatie_id && Array.isArray(a.plan.bronnen) ? { plan: a.plan } : {}),
      ...(Array.isArray(a.goedkeuring_nodig) ? { goedkeuring_nodig: a.goedkeuring_nodig
        .filter((v: unknown): v is string => typeof v === 'string').map((v: string) => v.trim()).filter(Boolean) } : {}),
      ...(typeof a.schrijfversie === 'string' && a.schrijfversie.trim() ? { schrijfversie: a.schrijfversie.trim() } : {}),
      positie: typeof a.positie === 'string' ? a.positie : '',
      compensatie: typeof a.compensatie === 'string' ? a.compensatie : '',
      waarom: typeof a.waarom === 'string' ? a.waarom : '',
      valkuilen: Array.isArray(a.valkuilen) ? a.valkuilen.filter((v: unknown) => typeof v === 'string') : [],
    };
  } catch {
    return null;
  }
}

interface InboxState {
  // List view
  conversations: Conversation[];
  isLoading: boolean;
  isLoadingMore: boolean;
  unreadCount: number;
  nieuwCount: number;
  inBehandelingCount: number;
  bounceCount: number;
  missedCallCount: number;
  mijnCount: number;
  laterCount: number;
  teamMembers: TeamMember[];
  brandFilter: string;
  statusFilter: string;
  channelFilter: 'all' | 'email' | 'whatsapp' | 'phone';
  /** Baan-tab (migratie 235): 'klant' (standaard, incl. legacy), 'all' of één baan. */
  baanFilter: string;
  /** Open gesprekken per baan binnen het zichtveld; alleen gevuld als de baan-tabs zichtbaar zijn. */
  baanTellers: Record<string, number> | null;
  /** Door ThreadList gezet (admin): dan vraagt de store tellers per baan op. */
  baanTabsZichtbaar: boolean;
  searchQuery: string;
  total: number;
  page: number;
  hasMore: boolean;

  // Thread view
  activeThread: {
    conversation: Conversation;
    messages: Message[];
    attachments: Attachment[];
    customer: CustomerMatch | null;
    afhandelmeting_beschikbaar?: boolean;
    multibron_beschikbaar?: boolean;
    afdoening_beschikbaar?: boolean;
  } | null;
  isLoadingThread: boolean;
  /** Klantinfo-refresh loopt (vernieuwen-knop). */
  isRefreshingCustomer: boolean;
  /** Inbox V2: gesprek openen zonder op de klantmatch te wachten; klantkaart komt apart. */
  klantLater: boolean;
  setKlantLater: (v: boolean) => void;
  /**
   * V3 (14-09): concepten en kaarten komen van de lezer (/api/inbox/lees), niet
   * van de oude draft-generator. generateDraft en de herlezing na een Ja lopen
   * dan via leesOpnieuw. Gezet door InboxV3Page; classic laat hem uit.
   */
  lezerModus: boolean;
  setLezerModus: (v: boolean) => void;
  /** Herlees de laatste klantmail met de lezer en herlaad het gesprek (concept + kaarten vers). */
  leesOpnieuw: (instructie?: string, huidigConcept?: string) => Promise<void>;

  // AI draft
  aiDraft: string | null;
  aiDraftAnalysis: string | null;
  aiDraftConfidence: number | null;
  aiDraftConfidenceReason: string | null;
  aiDraftModel: string | null;
  aiDraftError: string | null;
  isGeneratingDraft: boolean;
  /** 'prebaked' = auto-loaded from messages.ai_draft_text bij openThread; 'on-demand' = via generateDraft button. */
  aiDraftSource: 'prebaked' | 'on-demand' | null;
  aiActieContext: string | null;
  aiVraagplan: MailVraagplan | null;
  /**
   * Voorgestelde agent-acties bij de draft (mail-agent afnemer 2). Komt uit
   * messages.ai_acties (prebaked) of de draft-response (on-demand). Chips zijn
   * display-only: uitvoering gebeurt server-side op het verzendmoment.
   */
  aiActies: VoorgesteldeActie[] | null;
  /** Modus per mail-verb (uit /api/inbox/thread); 'voorbereiden' = kaart met Akkoord/Afwijzen. */
  mailVerbModi: MailVerbModi;
  /** Index van de actie waarvan het besluit onderweg is (knoppen disabled). */
  actieBusyIndex: number | null;
  actieError: string | null;
  aiActiesMessageId: string | null;

  // Klacht-advies (intern) — het zwaardere Opus-pad. De klant-draft komt via
  // aiDraft (ai_draft_text); dit is het losse tactische advies dat NOOIT naar
  // de klant gaat.
  klachtAdvies: KlachtAdvies | null;
  isGeneratingKlachtAdvies: boolean;
  klachtBeoordelingBezig: boolean;
  klachtAdviesError: string | null;

  // Split-screen UI state
  klantinfoOpen: boolean;
  autoAdvanceEnabled: boolean;

  // Reply
  isSending: boolean;

  // Polling
  pollingIntervalId: ReturnType<typeof setInterval> | null;

  // Sync health
  lastSyncedAt: string | null;
  isSyncStale: boolean;
  threadsError: string | null;

  // Reply
  sendError: string | null;
  /** Verzending gelukt, maar een administratieve vervolgstap vraagt controle. */
  sendWarning: string | null;

  // Actions
  fetchConversations: () => Promise<void>;
  loadMore: () => Promise<void>;
  openThread: (conversationId: string) => Promise<void>;
  /**
   * Akkoord of afwijzing van een voorgestelde mail-agent-actie (trede 2).
   * Stuurt alleen message_id + index + besluit (+ reden/keuze); de server
   * leest het voorstel zelf. Optimistic uitkomst met rollback.
   */
  besluitActie: (
    index: number,
    besluit: 'akkoord' | 'afgewezen',
    reden?: { code: AfwijsRedenCode; tekst?: string },
    keuze?: { optie_nummer?: number; klacht_rit_id?: string; klacht_keuze_gecontroleerd?: boolean },
    /** V3 "Ja + versturen": meerdere kaarten achter elkaar, één herlezing daarna. */
    opts?: { geenHerlezing?: boolean },
  ) => Promise<void>;
  verversFactuurconcept: (index: number) => Promise<void>;
  heropenAfdoening: (index: number) => Promise<void>;
  /** Convenience wrapper: null = closeThread, string = openThread. Voor split-screen UI. */
  setActiveThread: (conversationId: string | null) => Promise<void>;
  /** Selecteer volgende open thread in lijst en open 'm. Returns gekozen ID of null. */
  selectNextThread: () => Promise<string | null>;
  /** Selecteer vorige open thread. */
  selectPrevThread: () => Promise<string | null>;
  /** Toggle klantinfo slide-out. */
  toggleKlantinfo: () => void;
  /**
   * Herlaad de actieve thread (klantinfo + orders vers uit SF) zónder de
   * AI-draft/composer-state te resetten. Voor na een out-of-band mutatie
   * (bv. net een nieuwe opdracht aangemaakt op een ander tabblad).
   */
  refreshCustomerInfo: () => Promise<void>;
  /** Persist auto-advance voorkeur in localStorage. */
  setAutoAdvanceEnabled: (enabled: boolean) => void;
  closeThread: () => void;
  sendReply: (
    html: string,
    text: string,
    opts?: { to?: string; cc?: string; bcc?: string; from_brand?: string; actie_context?: string | null; handmatig_antwoord?: boolean; afhandel_keuze?: import('../lib/mail-afhandelmeting').MailAfhandelKeuze; afhandel_bronnen?: import('../lib/mail-afhandelmeting').MailAfhandelBron[] },
    files?: File[],
  ) => Promise<void>;
  generateDraft: (extraContext?: string, nieuweActies?: boolean) => Promise<void>;
  /** Genereer het tactische klacht-advies + concept (Opus). Vult klachtAdvies + aiDraft. */
  generateKlachtAdvies: (extraContext?: string, expectedBehandeling?: { id: string; versie: number }) => Promise<void>;
  saveKlachtBeoordeling: (
    behandeling: KlachtBehandeling,
    beoordeling: Omit<KlachtBeoordeling, 'door' | 'at'>,
  ) => Promise<{ behandeling?: KlachtBehandeling; error?: string; conceptGemaakt: boolean }>;
  updateStatus: (id: string, status: string) => Promise<void>;
  updateSubject: (id: string, subject: string) => Promise<void>;
  /** Snooze (ISO-moment) of unsnooze (null). Optimistic + rollback. */
  snoozeConversation: (id: string, until: string | null) => Promise<void>;
  /** Laad teamleden voor de toewijs-picker (eenmalig; no-op als al gevuld). */
  fetchTeam: () => Promise<void>;
  /** Wijs gesprek toe aan collega (email) of ontwijs (null). Optimistic + rollback. */
  assignConversation: (id: string, assigneeEmail: string | null) => Promise<void>;
  updateLeadField: (leadId: string, fields: Record<string, string>) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
  setBrandFilter: (brand: string) => void;
  setStatusFilter: (status: string) => void;
  setChannelFilter: (channel: 'all' | 'email' | 'whatsapp' | 'phone') => void;
  setBaanFilter: (baan: string) => void;
  setBaanTabsZichtbaar: (zichtbaar: boolean) => void;
  setSearchQuery: (query: string) => void;
  linkCustomerToConversation: (params: {
    conversationId: string;
    customerId: string;
    customerType: 'account' | 'lead';
    customerName: string;
    contactId?: string;
    contactName?: string;
    rememberDomain?: boolean;
    /** Default 'manual'. Pass 'suggestion_accepted' wanneer click op auto-suggest. */
    matchSignal?: 'manual' | 'suggestion_accepted';
  }) => Promise<void>;
  unlinkCustomerFromConversation: (conversationId: string) => Promise<void>;
  startPolling: (intervalMs?: number) => void;
  stopPolling: () => void;
}

export const useInboxStore = create<InboxState>()((set, get) => ({
  conversations: [],
  isLoading: false,
  isLoadingMore: false,
  unreadCount: 0,
  nieuwCount: 0,
  inBehandelingCount: 0,
  bounceCount: 0,
  missedCallCount: 0,
  mijnCount: 0,
  laterCount: 0,
  teamMembers: [],
  brandFilter: 'all',
  statusFilter: 'open',
  channelFilter: 'all',
  baanFilter: 'klant',
  baanTellers: null,
  baanTabsZichtbaar: false,
  searchQuery: '',
  total: 0,
  page: 1,
  hasMore: false,

  activeThread: null,
  isLoadingThread: false,
  isRefreshingCustomer: false,
  klantLater: false,
  setKlantLater: (v: boolean) => set({ klantLater: v }),
  lezerModus: false,
  setLezerModus: (v: boolean) => set({ lezerModus: v }),
  leesOpnieuw: async (instructie?: string, huidigConcept?: string) => {
    const { activeThread } = get();
    if (!activeThread) return;
    if (get().actieBusyIndex !== null || get().isGeneratingDraft) {
      set({ aiDraftError: 'Wacht tot de actie of het concept klaar is.' }); return;
    }
    const conversationId = activeThread.conversation.id;
    set({ isGeneratingDraft: true, aiDraftError: null });
    try {
      await apiFetch('/api/inbox/lees', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id: conversationId,
          instructie: instructie?.trim() || undefined,
          // Alleen bij een instructie is het huidige concept context ("korter", "geen klacht").
          huidig_concept: instructie?.trim() ? (huidigConcept ?? get().aiDraft ?? undefined) : undefined,
        }),
      });
      if (get().activeThread?.conversation.id !== conversationId) return;
      // De lezer schrijft op het bericht; het gesprek herladen leest concept en kaarten terug.
      await get().openThread(conversationId);
    } catch (error) {
      console.error('[Inbox] leesOpnieuw error:', error);
      set({ aiDraftError: error instanceof Error ? error.message : 'Opnieuw lezen mislukt' });
    } finally {
      set({ isGeneratingDraft: false });
    }
  },

  aiDraft: null,
  aiDraftAnalysis: null,
  aiDraftConfidence: null,
  aiDraftConfidenceReason: null,
  aiDraftModel: null,
  aiDraftError: null,
  isGeneratingDraft: false,
  aiDraftSource: null,
  aiActieContext: null,
  aiVraagplan: null,
  aiActies: null,
  mailVerbModi: {},
  actieBusyIndex: null,
  actieError: null,
  aiActiesMessageId: null,
  klachtAdvies: null,
  isGeneratingKlachtAdvies: false,
  klachtBeoordelingBezig: false,
  klachtAdviesError: null,

  klantinfoOpen: false,
  autoAdvanceEnabled: readAutoAdvancePref(),

  isSending: false,
  sendError: null,
  sendWarning: null,

  pollingIntervalId: null,

  lastSyncedAt: null,
  isSyncStale: false,
  threadsError: null,

  fetchConversations: async () => {
    // Replace-mode: refresh page 1 of current filter set. Used by polling,
    // initial load, and filter-change. Does NOT preserve previously
    // loaded follow-on pages — `loadMore` is responsible for those.
    const { brandFilter, statusFilter, channelFilter, searchQuery, baanFilter, baanTabsZichtbaar } = get();
    // Polling shouldn't blow away pages 2+ that the rep clicked open. If
    // we're past page 1, refresh in place (replace-from-1 + drop tail).
    set({ isLoading: true, page: 1 });
    try {
      const params = new URLSearchParams({
        brand: brandFilter,
        status: searchQuery ? 'all' : statusFilter,
        page: '1',
      });
      if (channelFilter !== 'all') params.set('channel', channelFilter);
      if (searchQuery) params.set('search', searchQuery);
      if (baanFilter !== 'klant') params.set('baan', baanFilter);
      if (baanTabsZichtbaar) params.set('baan_tellers', '1');
      const data = await apiFetch(`/api/inbox/threads?${params}`);
      const conversations = (data.conversations || []) as Conversation[];
      const unreadCount = conversations.filter((c: Conversation) => c.has_unread).length;
      const lastSyncedAt = data.lastSyncedAt || null;
      const isSyncStale = lastSyncedAt
        ? (Date.now() - new Date(lastSyncedAt).getTime()) > 10 * 60 * 1000
        : false;
      set({
        conversations,
        unreadCount,
        threadsError: null,
        nieuwCount: data.nieuw_count || 0,
        inBehandelingCount: data.in_behandeling_count || 0,
        bounceCount: data.bounce_count || 0,
        missedCallCount: data.missed_call_count || 0,
        mijnCount: data.mijn_count || 0,
        laterCount: data.later_count || 0,
        baanTellers: data.baan_tellers && typeof data.baan_tellers === 'object' ? data.baan_tellers : null,
        total: data.total || 0,
        hasMore: !!data.hasMore,
        lastSyncedAt,
        isSyncStale,
        isLoading: false,
      });
    } catch (error) {
      console.error('[Inbox] fetchConversations error:', error);
      set({ isLoading: false, threadsError: 'Het inboxoverzicht kon niet worden bijgewerkt. Getoonde statussen kunnen verouderd zijn.' });
    }
  },

  loadMore: async () => {
    const { brandFilter, statusFilter, channelFilter, searchQuery, page, hasMore, isLoadingMore, baanFilter } = get();
    if (!hasMore || isLoadingMore) return;
    const nextPage = page + 1;
    set({ isLoadingMore: true });
    try {
      const params = new URLSearchParams({
        brand: brandFilter,
        status: searchQuery ? 'all' : statusFilter,
        page: String(nextPage),
      });
      if (channelFilter !== 'all') params.set('channel', channelFilter);
      if (searchQuery) params.set('search', searchQuery);
      if (baanFilter !== 'klant') params.set('baan', baanFilter);
      const data = await apiFetch(`/api/inbox/threads?${params}`);
      const newConversations = (data.conversations || []) as Conversation[];
      set((state) => {
        // Dedup against current list (polling-race / overlap)
        const existingIds = new Set(state.conversations.map(c => c.id));
        const merged = [
          ...state.conversations,
          ...newConversations.filter(c => !existingIds.has(c.id)),
        ];
        return {
          conversations: merged,
          page: nextPage,
          hasMore: !!data.hasMore,
          total: data.total ?? state.total,
          isLoadingMore: false,
        };
      });
    } catch (error) {
      console.error('[Inbox] loadMore error:', error);
      set({ isLoadingMore: false, threadsError: 'Meer gesprekken laden is mislukt. Ververs het overzicht om de actuele statussen te zien.' });
    }
  },

  openThread: async (conversationId: string) => {
    set({ isLoadingThread: true, aiDraft: null, aiDraftAnalysis: null, aiDraftConfidence: null, aiDraftConfidenceReason: null, aiDraftModel: null, aiDraftError: null, aiDraftSource: null, aiActies: null, aiVraagplan: null, aiActieContext: null, actieBusyIndex: null, actieError: null, klachtAdvies: null, klachtAdviesError: null, sendError: null, sendWarning: null });
    try {
      const klantLater = get().klantLater;
      const data = await apiFetch(`/api/inbox/thread?id=${conversationId}${klantLater ? '&klant=later' : ''}`);
      const messages: Message[] = data.messages || [];

      // Auto-load pre-baked draft van laatste inkomende message (als aanwezig).
      // Caller (composer) ziet aiDraft + aiDraftSource='prebaked' bij mount → "✨ Concept klaar".
      let prebakedDraft: string | null = null;
      let prebakedConfidence: number | null = null;
      let prebakedConfidenceReason: string | null = null;
      let prebakedModel: string | null = null;
      let prebakedActies: VoorgesteldeActie[] | null = null;
      let prebakedActieContext: string | null = null;
      let prebakedVraagplan: MailVraagplan | null = null;
      let prebakedMessageId: string | null = null;
      let prebakedKlachtAdvies: KlachtAdvies | null = null;
      // Dezelfde centrale unie van inbound- en relaybronnen als de server.
      // Een gelijke nieuwste timestamp blijft bewust onbeantwoordbaar.
      const bronkeuze = kiesActueleKlantbronBasis(messages, data.conversation);
      if (bronkeuze.status === 'gevonden') {
        const m = bronkeuze.bron.bericht as Message;
        // Ook een kaart zonder concept telt (WhatsApp: de lezer laat het concept
        // leeg als de opmerking geblokkeerd is en de kaart zegt waarom; Marijke
        // 14-09 zag daardoor niets). Voorheen alleen bij tekst of afdoening.
        // Al beantwoord ná de bron: het concept is verbruikt en komt niet terug in
        // de composer (Travelbase 17-09: "Verstuur" naast een verzonden antwoord).
        // Kaarten en samenvatting blijven aan het bronbericht hangen.
        const beantwoord = isKlantBeantwoordNa(messages, m);
        if ((m.ai_draft_text || (Array.isArray(m.ai_acties) && m.ai_acties.length > 0)) && m.ai_draft_status === 'generated') {
          prebakedDraft = beantwoord ? null : (m.ai_draft_text || null);
          prebakedMessageId = m.id;
          prebakedConfidence = m.ai_draft_confidence ?? null;
          prebakedConfidenceReason = m.ai_draft_confidence_reason ?? null;
          prebakedModel = m.ai_draft_model ?? null;
          const acties = leesActies(m.ai_acties);
          prebakedActies = acties.length > 0 ? acties : null;
          prebakedActieContext = m.ai_actie_context ?? null;
          prebakedVraagplan = leesVraagplan(m.ai_vraagplan);
          // Klacht-advies (indien het klacht-pad al liep op deze message).
          if (m.ai_klacht_advies_status === 'generated' && m.ai_klacht_advies_generated_at
            && m.ai_draft_generated_at && Date.parse(m.ai_klacht_advies_generated_at) >= Date.parse(m.ai_draft_generated_at)) {
            prebakedKlachtAdvies = parseKlachtAdvies(m.ai_klacht_advies);
          }
        }
      }

      set({
        activeThread: {
          conversation: data.conversation,
          messages,
          attachments: data.attachments || [],
          customer: data.customer || null,
          afhandelmeting_beschikbaar: data.afhandelmeting_beschikbaar === true,
          multibron_beschikbaar: data.multibron_beschikbaar === true,
          afdoening_beschikbaar: data.afdoening_beschikbaar === true,
        },
        aiActiesMessageId: prebakedMessageId,
        aiDraft: prebakedDraft,
        aiDraftConfidence: prebakedConfidence,
        aiDraftConfidenceReason: prebakedConfidenceReason,
        aiDraftModel: prebakedModel,
        aiDraftSource: prebakedDraft ? 'prebaked' : null,
        aiActies: prebakedActies,
        aiActieContext: prebakedActieContext,
        aiVraagplan: prebakedVraagplan,
        mailVerbModi: (data.mail_verb_modi && typeof data.mail_verb_modi === 'object' ? data.mail_verb_modi : {}) as MailVerbModi,
        klachtAdvies: prebakedKlachtAdvies,
        isLoadingThread: false,
      });
      // V3: een open gesprek zonder concept én zonder kaart (queue-job dood door
      // een snel tweede bericht, mail van vóór de flip) laat de lezer bij het
      // openen één keer zelf lezen; kantoor hoeft niet op "Concept maken" te
      // klikken. Niet voor afgehandelde gesprekken of een bewuste lege uitkomst.
      if (get().lezerModus && !prebakedDraft && !prebakedActies && bronkeuze.status === 'gevonden'
        && ['nieuw', 'in_behandeling'].includes(String(data.conversation.status))
        && (bronkeuze.bron.bericht as Message).ai_draft_status !== 'generated'
        && !autoLeesGedaan.has(String((bronkeuze.bron.bericht as Message).id))) {
        autoLeesGedaan.add(String((bronkeuze.bron.bericht as Message).id));
        void get().leesOpnieuw();
      }
      // V3: een blokkade op een kaart is een momentopname ("niet gekoppeld"
      // terwijl de koppeling er inmiddels is). Bij het openen laat de server
      // zulke kaarten opnieuw controleren, zonder modelcall; één keer per bericht.
      if (get().lezerModus && prebakedMessageId && prebakedActies?.some((a) => !a.uitkomst && a.preview?.blokkade && !/^Wacht op gegevens/.test(a.preview.samenvatting || ''))
        && !herpreviewGedaan.has(prebakedMessageId)) {
        herpreviewGedaan.add(prebakedMessageId);
        const messageId = prebakedMessageId;
        void apiFetch('/api/inbox/lees', { method: 'POST', body: JSON.stringify({ conversation_id: conversationId, message_id: messageId, alleen_preview: true }) })
          .then((p) => {
            if (!p?.ok || !Array.isArray(p.acties) || p.vernieuwd === 0) return;
            if (get().activeThread?.conversation.id !== conversationId || get().aiActiesMessageId !== messageId) return;
            const acties = leesActies(p.acties);
            set((s) => ({
              aiActies: acties.length > 0 ? acties : null,
              activeThread: s.activeThread ? { ...s.activeThread, messages: s.activeThread.messages.map((m) => m.id === messageId ? { ...m, ai_acties: p.acties } : m) } : s.activeThread,
            }));
          })
          .catch((e) => console.warn('[Inbox] herpreview faalde:', e));
      }
      if (klantLater) {
        // Klantkaart apart: het gesprek staat al, de match (2 s) komt erachteraan.
        void apiFetch(`/api/inbox/thread?id=${conversationId}&alleen=klant`).then((k) => {
          set((state) => state.activeThread?.conversation.id === conversationId
            ? { activeThread: { ...state.activeThread, customer: k.customer || null } }
            : {});
        }).catch((e) => console.error('[Inbox] klantkaart laden faalde:', e));
      }

      // Update unread count locally
      set((state) => ({
        conversations: state.conversations.map((c) =>
          c.id === conversationId ? { ...c, has_unread: false } : c
        ),
        unreadCount: Math.max(0, state.unreadCount - (
          state.conversations.find(c => c.id === conversationId)?.has_unread ? 1 : 0
        )),
      }));
    } catch (error) {
      console.error('[Inbox] openThread error:', error);
      set({ isLoadingThread: false });
    }
  },

  besluitActie: async (index, besluit, reden, keuze, opts) => {
    const state = get();
    const thread = state.activeThread;
    const acties = state.aiActies;
    if (!thread || !acties || !acties[index] || acties[index]!.uitkomst || state.actieBusyIndex !== null) return;
    if (state.isGeneratingDraft || state.klachtBeoordelingBezig) {
      set({ actieError: 'Wacht tot het nieuwe concept klaar is voordat je een actie beoordeelt.' });
      return;
    }
    // De expliciete bronbinding komt uit openThread of de draftresponse.
    const messageId = actieBronId(thread.messages, state.aiActiesMessageId, thread.conversation);
    if (!messageId) {
      set({ actieError: 'Dit voorstel is niet aan een bericht gekoppeld. Genereer het concept opnieuw.' });
      return;
    }

    const vorige = acties;
    set({ actieBusyIndex: index, actieError: null });
    try {
      const data = await apiFetch('/api/inbox/actie', {
        method: 'POST',
        body: JSON.stringify({ message_id: messageId, actie_index: index, besluit, reden, keuze,
          voorstel: { verb: acties[index]!.verb, params: acties[index]!.params, preview: acties[index]!.preview ?? null,
            ...(acties[index]!.vraag ? { vraag: acties[index]!.vraag } : {}) } }),
      });
      if (get().activeThread?.conversation.id !== thread.conversation.id || get().aiActiesMessageId !== messageId) return;
      set((s) => ({
        aiActies: (s.aiActies ?? acties).map((a, i) => (i === index ? { ...a, uitkomst: data.uitkomst } : a)),
        actieBusyIndex: null,
        aiActieContext: null,
        // Ook in de message-rij, zodat een herlaad uit state consistent is.
        activeThread: s.activeThread
          ? {
              ...s.activeThread,
              messages: s.activeThread.messages.map((m) =>
                m.id === messageId && Array.isArray(m.ai_acties)
                  ? { ...m, ai_acties: m.ai_acties.map((a, i) => (i === index ? { ...a, uitkomst: data.uitkomst } : a)) }
                  : m,
              ),
            }
          : s.activeThread,
      }));
      if (acties[index]!.verb === AFDOENING_VERB && besluit === 'akkoord') {
        const proof = leesMailAfdoening(data.uitkomst?.afdoening);
        if (data.uitkomst?.ok === true && proof?.messageId === messageId && proof.conversationId === thread.conversation.id) {
          set(s => {
            const shouldRemove = !statusPastBijFilter(s.statusFilter, 'afgehandeld');
            const wasListed = s.conversations.some(c => c.id === thread.conversation.id);
            return {
              aiDraft: null, aiActieContext: null,
              activeThread: s.activeThread ? { ...s.activeThread,
                conversation: { ...s.activeThread.conversation, status: 'afgehandeld', has_unread: false },
                messages: s.activeThread.messages.map(m => m.id === messageId ? { ...m, ai_draft_text: '', ai_actie_context: null } : m),
              } : null,
              conversations: shouldRemove ? s.conversations.filter(c => c.id !== thread.conversation.id)
                : s.conversations.map(c => c.id === thread.conversation.id ? { ...c, status: 'afgehandeld', has_unread: false } : c),
              nieuwCount: Math.max(0, s.nieuwCount - Number(thread.conversation.status === 'nieuw')),
              inBehandelingCount: Math.max(0, s.inBehandelingCount - Number(thread.conversation.status === 'in_behandeling')),
              total: Math.max(0, s.total - Number(shouldRemove && wasListed)),
            };
          });
        } else set({ actieError: data.uitkomst?.uitleg || 'Afdoening niet bevestigd. Herlaad en controleer het gesprek.' });
        return; // No regenerated reply, including on an uncertain close.
      }
      if (get().lezerModus) {
        // V3: na een geslaagde Ja het antwoord herschrijven met de echte uitkomst
        // (referentie, link) in plaats van een belofte; de lezer bewaart de
        // besliste acties. Een Nee of een mislukte actie laat het concept staan.
        if (besluit === 'akkoord' && data.uitkomst?.ok === true && !opts?.geenHerlezing) {
          await get().leesOpnieuw('De actie is zojuist uitgevoerd. Verwerk de uitkomst (referentie, link) concreet in het antwoord; beloof niets meer wat al gebeurd is.');
        }
      } else if (get().aiVraagplan || get().aiActies?.every((a) => a.uitkomst)) {
        await get().generateDraft(undefined, false);
      }
    } catch (error) {
      console.error('[Inbox] besluitActie error:', error);
      if (get().activeThread?.conversation.id !== thread.conversation.id || get().aiActiesMessageId !== messageId) return;
      set({ aiActies: vorige, actieBusyIndex: null, actieError: error instanceof Error ? error.message : 'Besluit mislukt' });
    }
  },

  heropenAfdoening: async (index) => {
    const state = get(), thread = state.activeThread, actie = state.aiActies?.[index];
    const proof = leesMailAfdoening(actie?.uitkomst?.afdoening);
    if (!thread || !actie || actie.verb !== AFDOENING_VERB || !proof || proof.messageId !== state.aiActiesMessageId
      || proof.conversationId !== thread.conversation.id || state.actieBusyIndex !== null || state.isSending) return;
    if (state.isGeneratingDraft || state.isGeneratingKlachtAdvies || state.klachtBeoordelingBezig) {
      set({ actieError: 'Wacht tot de lopende beoordeling klaar is voordat je de afdoening heropent.' }); return;
    }
    set({ actieBusyIndex: index, actieError: null });
    try {
      const data = await apiFetch('/api/inbox/actie', { method: 'POST', body: JSON.stringify({
        message_id: proof.messageId, actie_index: index, besluit: 'heropen_afdoening',
        voorstel: { verb: actie.verb, params: actie.params, preview: actie.preview ?? null, uitkomst: actie.uitkomst,
          ...(actie.vraag ? { vraag: actie.vraag } : {}) },
      }) });
      const herstel = leesMailAfdoeningHerstel(data.herstel);
      if (!herstel || herstel.messageId !== proof.messageId || herstel.besluitId !== proof.besluitId
        || herstel.conversationId !== proof.conversationId) throw new Error('Heropenen niet bevestigd. Herlaad en controleer het gesprek.');
      if (get().activeThread?.conversation.id !== thread.conversation.id) return;
      // Read the committed state before any new generation. Never clear a decision optimistically.
      await get().openThread(thread.conversation.id);
      const reloaded = get();
      if (reloaded.activeThread?.conversation.id !== thread.conversation.id || reloaded.isLoadingThread
        || !reloaded.activeThread.messages.some(m => m.id === proof.messageId && leesMailAfdoeningHerstel(m.ai_afdoening_herstel)?.besluitId === proof.besluitId)) {
        throw new Error('Heropenen opgeslagen; herladen nog niet bevestigd. Controleer het gesprek.');
      }
      const status = reloaded.activeThread.conversation.status;
      set(s => {
        const shouldRemove = !statusPastBijFilter(s.statusFilter, status);
        const wasListed = s.conversations.some(c => c.id === thread.conversation.id);
        return {
          conversations: shouldRemove ? s.conversations.filter(c => c.id !== thread.conversation.id)
            : s.conversations.map(c => c.id === thread.conversation.id ? { ...c, status } : c),
          nieuwCount: Math.max(0,s.nieuwCount + Number(status === 'nieuw') - Number(thread.conversation.status === 'nieuw')),
          inBehandelingCount: Math.max(0,s.inBehandelingCount + Number(status === 'in_behandeling') - Number(thread.conversation.status === 'in_behandeling')),
          total: Math.max(0,s.total - Number(shouldRemove && wasListed)),
        };
      });
      if (status !== 'in_behandeling') throw new Error('Herstel is opgeslagen, maar de gespreksstatus is intussen gewijzigd. Controleer eerst het gesprek.');
      await get().generateDraft(undefined, true);
    } catch (e) {
      if (get().activeThread?.conversation.id === thread.conversation.id) set({ actieError: e instanceof Error ? e.message : 'Heropenen mislukt.' });
    } finally {
      if (get().activeThread?.conversation.id === thread.conversation.id) set({ actieBusyIndex: null });
    }
  },

  verversFactuurconcept: async (index) => {
    const state = get(), thread = state.activeThread, actie = state.aiActies?.[index];
    const opnieuwVoorbereidbaar = ['bereid_factuurconcept','bereid_offerteconcept'].includes(actie?.verb ?? '')
      || (actie?.verb === 'bereid_partnerdoorverwijzing'
        && actie.uitkomst?.besluit === 'akkoord'
        && actie.uitkomst.ok === false
        && actie.uitkomst.partner_herstel?.status === 'pre_write_mislukt');
    if (!thread || !actie || !opnieuwVoorbereidbaar || state.actieBusyIndex !== null || state.isSending) return;
    if (state.isGeneratingDraft || state.isGeneratingKlachtAdvies || state.klachtBeoordelingBezig) {
      set({ actieError: 'Wacht tot het concept klaar is voordat je de actie opnieuw voorbereidt.' }); return;
    }
    const messageId = actieBronId(thread.messages, state.aiActiesMessageId, thread.conversation);
    if (!messageId) { set({ actieError: 'Herlaad eerst het actuele bronbericht.' }); return; }
    set({ actieBusyIndex: index, actieError: null });
    try {
      const data = await apiFetch('/api/inbox/actie', { method: 'POST', body: JSON.stringify({
        message_id: messageId, actie_index: index, besluit: 'opnieuw_voorbereiden',
        voorstel: { verb: actie.verb, params: actie.params, preview: actie.preview ?? null, uitkomst: actie.uitkomst ?? null,
          ...(actie.vraag ? { vraag: actie.vraag } : {}) },
      }) });
      if (get().activeThread?.conversation.id !== thread.conversation.id || get().aiActiesMessageId !== messageId) return;
      set(s => ({ aiActies: data.acties, aiActieContext: null, actieBusyIndex: null, sendError: null,
        activeThread: s.activeThread ? { ...s.activeThread, messages: s.activeThread.messages.map(m => m.id === messageId
          ? { ...m, ai_acties: data.acties, ai_actie_context: null } : m) } : s.activeThread }));
      // De nieuwe preview vereist opnieuw een expliciete akkoord-/afwijsklik.
      // Bij partnerherstel ontstaat hier dus nog geen aanvraag of verzending.
    } catch (error) {
      if (get().activeThread?.conversation.id !== thread.conversation.id || get().aiActiesMessageId !== messageId) return;
      set({ actieBusyIndex: null, actieError: error instanceof Error ? error.message : 'Opnieuw voorbereiden mislukt' });
    }
  },

  setActiveThread: async (conversationId: string | null) => {
    if (conversationId === null) {
      get().closeThread();
      return;
    }
    const vorige = get().activeThread?.conversation.id ?? null;
    if (vorige === conversationId) return;
    // Naar een ander gesprek: de drawer (order/lead/klant) hoort bij het vorige
    // gesprek en blijft anders open naast de nieuwe mail (Willem 15-09: mail
    // van Jetty met de order van Guus ernaast). Bij een deep link (geen vorig
    // gesprek) blijft ?open= staan.
    if (vorige) useInboxDrawerStore.getState().close();
    await get().openThread(conversationId);
  },

  refreshCustomerInfo: async () => {
    const active = get().activeThread;
    if (!active || get().isRefreshingCustomer) return;
    const conversationId = active.conversation.id;
    set({ isRefreshingCustomer: true });
    try {
      const data = await apiFetch(`/api/inbox/thread?id=${conversationId}`);
      set((state) => {
        // Gebruiker kan intussen naar een andere thread zijn genavigeerd
        if (state.activeThread?.conversation.id !== conversationId) {
          return { isRefreshingCustomer: false };
        }
        return {
          activeThread: {
            conversation: data.conversation,
            messages: data.messages || [],
            attachments: data.attachments || [],
            customer: data.customer || null,
          },
          isRefreshingCustomer: false,
        };
      });
    } catch (error) {
      console.error('[Inbox] refreshCustomerInfo error:', error);
      set({ isRefreshingCustomer: false });
    }
  },

  selectNextThread: async () => {
    const { activeThread, conversations } = get();
    if (!activeThread) return null;
    const nextId = findAdjacentThreadId(conversations, activeThread.conversation.id, 'next');
    if (nextId) await get().openThread(nextId);
    return nextId;
  },

  selectPrevThread: async () => {
    const { activeThread, conversations } = get();
    if (!activeThread) return null;
    const prevId = findAdjacentThreadId(conversations, activeThread.conversation.id, 'prev');
    if (prevId) await get().openThread(prevId);
    return prevId;
  },

  toggleKlantinfo: () => {
    set((s) => ({ klantinfoOpen: !s.klantinfoOpen }));
  },

  setAutoAdvanceEnabled: (enabled: boolean) => {
    writeAutoAdvancePref(enabled);
    set({ autoAdvanceEnabled: enabled });
  },

  closeThread: () => {
    // De drawer hoort bij het gesprek; zonder gesprek geen order ernaast.
    useInboxDrawerStore.getState().close();
    set({ activeThread: null, aiDraft: null, aiDraftAnalysis: null, aiDraftConfidence: null, aiDraftConfidenceReason: null, aiDraftModel: null, aiDraftError: null, aiDraftSource: null, sendError: null });
  },

  sendReply: async (
    html: string,
    text: string,
    opts?: { to?: string; cc?: string; bcc?: string; from_brand?: string; actie_context?: string | null; handmatig_antwoord?: boolean; afhandel_keuze?: import('../lib/mail-afhandelmeting').MailAfhandelKeuze; afhandel_bronnen?: import('../lib/mail-afhandelmeting').MailAfhandelBron[] },
    files?: File[],
  ) => {
    const state = get();
    const { activeThread, aiDraft, aiDraftAnalysis, aiDraftConfidence, aiDraftConfidenceReason, autoAdvanceEnabled } = state;
    if (!activeThread || state.isSending) return;
    if (state.klachtBeoordelingBezig) {
      set({ sendError: 'Wacht tot de beoordeling is opgeslagen en het nieuwe concept klaar is.' });
      return;
    }
    const sourceMessageId = actueleKlantbronId(activeThread.messages, activeThread.conversation);
    if (!sourceMessageId) {
      set({ sendError: 'Het actuele klantbericht kon niet eenduidig worden vastgesteld. Ververs het gesprek.' });
      return;
    }
    const handmatigAntwoord = opts?.handmatig_antwoord === true;
    let handmatigContext: string | undefined;
    if (!handmatigAntwoord && state.aiActiesMessageId === sourceMessageId && state.aiActies?.some(a => a.verb === AFDOENING_VERB && a.uitkomst?.besluit === 'akkoord')) {
      set({ sendError: 'Afdoening zonder antwoord is beoordeeld. Controleer het opgeslagen resultaat; verstuur geen oud antwoordconcept.' });
      return;
    }
    let factuurDocumenten: string[], offerteDocumenten: string[];
    try {
      const facturen = handmatigAntwoord ? [] : facturenUitActies(state.aiActies), offertes = handmatigAntwoord ? [] : offertesUitActies(state.aiActies);
      const docs = [...facturen,...offertes];
      if (docs.length && state.aiActiesMessageId !== sourceMessageId) throw new Error('Controleer de bijlagen bij het laatste klantbericht.');
      if (docs.reduce((n,d) => n + d.grootte,0) + (files || []).reduce((n,f) => n + f.size,0) > MAX_MAIL_BIJLAGEN_BYTES) throw new Error(`Handmatige en voorbereide bijlagen zijn samen groter dan ${MAX_MAIL_BIJLAGEN_LABEL}.`);
      factuurDocumenten = facturen.map(factuurDocumentSleutel);
      offerteDocumenten = offertes.map(offerteDocumentSleutel);
    } catch (e) { set({ sendError: e instanceof Error ? e.message : 'Bijlagen controleren mislukt.' }); return; }

    // Controle vóór optimistic send/auto-advance: de medewerker blijft bij de echte uitkomst.
    if (activeThread.conversation.channel === 'email') {
      set({ isSending: true, sendError: null, sendWarning: null });
      try {
        const poort = await apiFetch('/api/inbox/reply', {
          method: 'POST', body: JSON.stringify({
            conversation_id: activeThread.conversation.id, body_html: html, body_text: text,
            prepare_only: true, actie_context: opts?.actie_context ?? null,
            handmatig_antwoord: handmatigAntwoord || undefined,
            afhandel_keuze: opts?.afhandel_keuze,
            afhandel_bronnen: opts?.afhandel_bronnen,
            source_message_id: sourceMessageId,
            factuur_documenten: factuurDocumenten,
            offerte_documenten: offerteDocumenten,
            handmatige_bijlagen_bytes: (files || []).reduce((n,f) => n + f.size,0),
            to: opts?.to, cc: opts?.cc, bcc: opts?.bcc,
          }),
        });
        if (!poort.ready) {
          set({ isSending: false, sendError: poort.error || 'Beoordeel eerst de acties.',
            ...(Array.isArray(poort.acties) ? { aiActies: leesActies(poort.acties) } : {}), aiActieContext: null });
          if (!handmatigAntwoord && poort.review_required && get().activeThread?.conversation.id === activeThread.conversation.id) {
            await get().generateDraft(undefined, false);
          }
          return;
        }
        if (handmatigAntwoord) {
          if (poort.medewerker_gecontroleerd !== true && (typeof poort.handmatig_context !== 'string' || !/^[a-f0-9]{64}$/.test(poort.handmatig_context))) {
            set({ isSending: false, sendError: 'De controle van het eigen antwoord is niet bevestigd. Er is niets verstuurd.' });
            return;
          }
          handmatigContext = poort.handmatig_context;
        }
      } catch (error) {
        set({ isSending: false, sendError: error instanceof Error ? error.message : 'Acties controleren mislukt.' });
        return;
      }
      set({ isSending: false });
      if (get().activeThread?.conversation.id !== activeThread.conversation.id) return;
    }

    const conversationId = activeThread.conversation.id;
    const prevStatus = activeThread.conversation.status;
    const channel = activeThread.conversation.channel;
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date().toISOString();

    // ── Snapshot voor rollback ──
    const snapshot = {
      activeThread,
      conversations: state.conversations,
      nieuwCount: state.nieuwCount,
      inBehandelingCount: state.inBehandelingCount,
      total: state.total,
    };

    // Bepaal next-thread BEFORE we mutate de lijst (zodat index nog klopt)
    // V3 (Gmail-indeling): na versturen terug naar de lijst, niet door naar
    // het volgende gesprek (keuze Willem 14-09). Bij een fout wordt het
    // gesprek heropend, zie de rollback hieronder.
    const terugNaarLijst = state.lezerModus;
    const nextThreadId = autoAdvanceEnabled && !terugNaarLijst
      ? findAdjacentThreadId(state.conversations, conversationId, 'next')
      : null;

    // ── Optimistic message-insert ──
    const tempMessage: Message = {
      id: tempId,
      conversation_id: conversationId,
      gmail_message_id: null,
      channel,
      external_id: null,
      is_incoming: false,
      from_email: '',
      from_name: null,
      to_email: opts?.to ?? null,
      cc_email: opts?.cc ?? null,
      subject: null,
      body_text: text,
      body_html: html,
      message_date: now,
      has_attachments: !!files?.length || !!factuurDocumenten.length || !!offerteDocumenten.length,
      ai_draft_text: null,
      ai_draft_status: null,
      _optimisticStatus: 'sending',
    };

    // ── Optimistic state-update: insert message, switch status, remove from filtered list ──
    set((s) => {
      const shouldRemove = !statusPastBijFilter(s.statusFilter, 'beantwoord');
      const updatedConvs = shouldRemove
        ? s.conversations.filter((c) => c.id !== conversationId)
        : s.conversations.map((c) =>
            c.id === conversationId ? { ...c, status: 'beantwoord' as const } : c
          );

      let { nieuwCount, inBehandelingCount, total } = s;
      if (prevStatus === 'nieuw') nieuwCount = Math.max(0, nieuwCount - 1);
      if (prevStatus === 'in_behandeling') inBehandelingCount = Math.max(0, inBehandelingCount - 1);
      if (shouldRemove) total = Math.max(0, total - 1);

      return {
        conversations: updatedConvs,
        nieuwCount,
        inBehandelingCount,
        total,
        isSending: true,
        sendError: null,
        activeThread: s.activeThread
          ? {
              ...s.activeThread,
              conversation: { ...s.activeThread.conversation, status: 'beantwoord' as const },
              messages: [...s.activeThread.messages, tempMessage],
            }
          : null,
      };
    });

    // ── Auto-advance (in-page state swap, geen route nav) ──
    // Houd eigen tekst in beeld tot ook de laatste servercontrole geslaagd is.
    if (nextThreadId && !handmatigAntwoord) {
      void get().setActiveThread(nextThreadId);
    } else if (terugNaarLijst && !handmatigAntwoord) {
      get().closeThread();
    }

    // ── Async API call (non-blocking voor de UX) ──
    try {
      const attachmentPayload = files?.length
        ? await Promise.all(
            files.map(async (f) => ({
              filename: f.name,
              mimeType: f.type || 'application/octet-stream',
              dataBase64: await fileToBase64(f),
            })),
          )
        : undefined;

      const verzonden = await apiFetch('/api/inbox/reply', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id: conversationId,
          body_html: html,
          body_text: text,
          actie_context: opts?.actie_context ?? null,
          handmatig_antwoord: handmatigAntwoord || undefined,
          handmatig_context: handmatigContext,
          afhandel_keuze: opts?.afhandel_keuze,
          afhandel_bronnen: opts?.afhandel_bronnen,
          source_message_id: sourceMessageId,
          to: opts?.to || undefined,
          cc: opts?.cc || undefined,
          bcc: opts?.bcc || undefined,
          from_brand: opts?.from_brand || undefined,
          attachments: attachmentPayload,
          factuur_documenten: factuurDocumenten,
          offerte_documenten: offerteDocumenten,
          ai_draft: aiDraft || undefined,
          ai_draft_analysis: aiDraftAnalysis || undefined,
          ai_draft_confidence: aiDraftConfidence ?? undefined,
          ai_draft_confidence_reason: aiDraftConfidenceReason || undefined,
        }),
      });

      const providerMessageId = typeof verzonden.message_id === 'string' && verzonden.message_id.trim()
        ? verzonden.message_id.trim()
        : null;

      // Success: markeer temp message als 'sent' (zichtbaar tot polling het echt vervangt
      // bij volgende thread-fetch). Reset draft-state zodat compose-area leeg is voor volgende thread.
      set((s) => ({
        isSending: false,
        sendWarning: typeof verzonden.warning === 'string' ? `${activeThread.conversation.subject || 'Antwoord'}: ${verzonden.warning}` : null,
        aiDraft: null,
        aiDraftSource: null,
        aiDraftError: null,
        aiDraftModel: null,
        activeThread: s.activeThread && s.activeThread.conversation.id === conversationId
          ? {
              ...s.activeThread,
              messages: s.activeThread.messages.map((m) =>
                m.id === tempId ? {
                  ...m,
                  _optimisticStatus: 'sent' as const,
                  ...(providerMessageId
                    ? activeThread.conversation.channel === 'whatsapp'
                      ? { external_id: providerMessageId }
                      : { gmail_message_id: providerMessageId }
                    : {}),
                } : m
              ),
            }
          : s.activeThread,
      }));
      if (nextThreadId && handmatigAntwoord && get().activeThread?.conversation.id === conversationId) {
        void get().setActiveThread(nextThreadId);
      } else if (terugNaarLijst && handmatigAntwoord && get().activeThread?.conversation.id === conversationId) {
        get().closeThread();
      }
    } catch (error) {
      console.error('[Inbox] sendReply error:', error);
      const msg = error instanceof Error ? error.message : 'Versturen mislukt';

      // ── Rollback ──
      set((s) => {
        const restoringActive = s.activeThread?.conversation.id === conversationId;
        return {
          isSending: false,
          sendError: msg,
          conversations: snapshot.conversations,
          nieuwCount: snapshot.nieuwCount,
          inBehandelingCount: snapshot.inBehandelingCount,
          total: snapshot.total,
          // Als rep nog op de oude thread zit, restore volledig. Als auto-advance al naar volgende ging,
          // laat de huidige activeThread staan (rep kan via toast terug navigeren).
          activeThread: restoringActive ? snapshot.activeThread : s.activeThread,
        };
      });

      // Als we auto-advanced waren, navigeer terug zodat rep de fout ziet en opnieuw kan versturen
      if ((nextThreadId || terugNaarLijst) && get().activeThread?.conversation.id !== conversationId) {
        void get().setActiveThread(conversationId);
      }
    }
  },

  generateDraft: async (extraContext?: string, nieuweActies = true) => {
    // V3: één generator, de lezer. De oude draft-generator blijft voor classic.
    if (get().lezerModus) { await get().leesOpnieuw(extraContext); return; }
    if (get().actieBusyIndex !== null || get().isGeneratingDraft || get().isGeneratingKlachtAdvies) {
      set({ aiDraftError: 'Wacht tot de actie of het concept klaar is.' }); return;
    }
    const { activeThread } = get();
    if (!activeThread) return;

    set({ isGeneratingDraft: true, aiDraftError: null });
    try {
      const data = await apiFetch('/api/inbox/draft', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id: activeThread.conversation.id,
          extra_context: extraContext || undefined,
          nieuwe_acties: nieuweActies,
        }),
      });
      if (get().activeThread?.conversation.id !== activeThread.conversation.id) {
        set({ isGeneratingDraft: false });
        return;
      }
      set({
        aiDraft: data.draft,
        klachtAdvies: data.advies || null,
        klachtAdviesError: null,
        aiActiesMessageId: typeof data.message_id === 'string' ? data.message_id : null,
        aiDraftAnalysis: data.analysis || null,
        aiDraftConfidence: data.confidence,
        aiDraftConfidenceReason: data.confidence_reason || null,
        aiDraftModel: data.model || null,
        aiDraftError: null,
        aiDraftSource: 'on-demand',
        aiActieContext: data.actie_context ?? null,
        aiVraagplan: leesVraagplan(data.vraagplan),
        aiActies: (() => { const a = leesActies(data.acties); return a.length > 0 ? a : null; })(),
        isGeneratingDraft: false,
      });

      // Bij e-mail loopt een lead-wijziging via een beoordeelde actiekaart.
      // Alleen een concept genereren is geen toestemming om de offerte te sluiten.
      if (activeThread.conversation.channel !== 'email' && !data.actie_context && (data.lead_status || data.lead_reden)) {
        const primaryLead = get().activeThread?.customer?.leads?.[0];
        if (primaryLead) {
          const fields: Record<string, string> = {};
          if (data.lead_status && data.lead_status !== primaryLead.status) {
            fields.Status = data.lead_status;
          }
          if (data.lead_reden && data.lead_reden !== primaryLead.redenNietGeintereseerd) {
            fields.Reden_niet_geintereseerd__c = data.lead_reden;
          }
          if (Object.keys(fields).length > 0) {
            get().updateLeadField(primaryLead.id, fields).catch(() => {});
          }
        }
      }
    } catch (error) {
      console.error('[Inbox] generateDraft error:', error);
      const msg = error instanceof Error ? error.message : 'AI concept genereren mislukt';
      set({ isGeneratingDraft: false, aiDraftError: msg });
    }
  },

  generateKlachtAdvies: async (extraContext?: string, expectedBehandeling?: { id: string; versie: number }) => {
    if (get().actieBusyIndex !== null || get().isGeneratingDraft || get().isGeneratingKlachtAdvies) {
      set({ klachtAdviesError: 'Wacht tot de actie of het concept klaar is.' }); return;
    }
    const state = get();
    const { activeThread } = state;
    if (!activeThread) return;
    const messageId = expectedBehandeling
      ? actieBronId(activeThread.messages, state.aiActiesMessageId, activeThread.conversation)
      : null;
    if (expectedBehandeling && (!messageId || state.isLoadingThread || state.klachtAdvies?.plan?.behandeling_id !== expectedBehandeling.id)) {
      set({ klachtAdviesError: 'Deze behandelversie is niet aan het getoonde klachtbericht gekoppeld. Ververs het gesprek.' });
      return;
    }
    const retryScope = (s: InboxState) => JSON.stringify({
      conversation: s.activeThread?.conversation.id,
      klant: [s.activeThread?.conversation.customer_id ?? null, s.activeThread?.conversation.customer_type ?? null,
        s.activeThread?.conversation.from_email ?? null, s.activeThread?.conversation.phone ?? null],
      message: s.aiActiesMessageId,
      inkomend: s.activeThread?.messages.filter(m => m.is_incoming).at(-1)?.id,
      generatie: s.klachtAdvies?.plan?.generatie_id,
      behandeling: s.klachtAdvies?.plan?.behandeling_id,
    });
    const snapshot = retryScope(state);
    const isCurrent = (s: InboxState) => s.activeThread?.conversation.id === activeThread.conversation.id
      && (!expectedBehandeling || (!s.isLoadingThread && retryScope(s) === snapshot));

    set({ isGeneratingKlachtAdvies: true, klachtAdviesError: null,
      ...(expectedBehandeling ? { klachtBeoordelingBezig: true } : {}) });
    try {
      const data = await apiFetch('/api/inbox/klacht-advies', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id: activeThread.conversation.id,
          extra_context: extraContext || undefined,
          ...(expectedBehandeling ? { message_id: messageId, expected_behandeling: expectedBehandeling } : {}),
        }),
      });
      if (!isCurrent(get())) {
        set({ isGeneratingKlachtAdvies: false }); return;
      }
      if (expectedBehandeling && (data.message_id !== messageId || data.advies?.plan?.behandeling_id !== expectedBehandeling.id
        || data.advies?.plan?.beoordeling_versie !== expectedBehandeling.versie)) {
        throw new Error('Het nieuwe concept hoort niet bij dit bericht en deze behandelversie. Ververs het gesprek.');
      }
      set({
        klachtAdvies: data.advies || null,
        // De klacht-draft komt als gewone draft binnen (via ai_draft_text) →
        // vul dezelfde composer-state zodat "Gebruiken"/D gewoon werken.
        aiDraft: data.draft,
        aiDraftAnalysis: null,
        aiDraftConfidence: data.confidence ?? null,
        aiDraftConfidenceReason: data.confidence_reason || null,
        aiDraftModel: data.model || null,
        aiDraftError: null,
        aiDraftSource: 'on-demand',
        aiActies: leesActies(data.acties),
        aiVraagplan: leesVraagplan(data.vraagplan),
        aiActieContext: data.actie_context ?? null,
        aiActiesMessageId: data.message_id ?? null,
        isGeneratingKlachtAdvies: false,
        klachtAdviesError: null,
      });
    } catch (error) {
      console.error('[Inbox] generateKlachtAdvies error:', error);
      const msg = error instanceof Error ? error.message : 'Klacht-advies genereren mislukt';
      set({ isGeneratingKlachtAdvies: false,
        ...(!expectedBehandeling || isCurrent(get()) ? { klachtAdviesError: msg, ...(expectedBehandeling ? { aiDraftError: msg } : {}) } : {}) });
    } finally {
      if (expectedBehandeling) set({ klachtBeoordelingBezig: false });
    }
  },

  saveKlachtBeoordeling: async (behandeling, beoordeling) => {
    const state = get();
    if (state.klachtBeoordelingBezig || state.actieBusyIndex !== null || state.aiActies?.some(a => a.uitvoering && !a.uitkomst) || state.isGeneratingDraft
      || state.isGeneratingKlachtAdvies || state.isSending || state.isLoadingThread) {
      return { error: 'Wacht tot de actie, verzending of het concept klaar is.', conceptGemaakt: false };
    }
    const thread = state.activeThread;
    const messageId = thread && actieBronId(thread.messages, state.aiActiesMessageId, thread.conversation);
    const plan = state.klachtAdvies?.plan;
    if (!thread || thread.conversation.channel !== 'email' || !messageId || plan?.behandeling_id !== behandeling.id) {
      return { error: 'Deze beoordeling is niet aan het getoonde klachtbericht gekoppeld. Ververs het gesprek.', conceptGemaakt: false };
    }
    const conversationId = thread.conversation.id;
    const behandelingId = behandeling.id;
    const generatieId = plan.generatie_id;
    const actueleBronId = actueleKlantbronId(thread.messages, thread.conversation);
    const binding = (c: Conversation) => JSON.stringify([
      c.customer_id ?? null, c.customer_type ?? null, c.from_email ?? null, c.phone ?? null,
    ]);
    const klantBinding = binding(thread.conversation);
    const isCurrent = (s: InboxState) => !!s.activeThread
      && !s.isLoadingThread
      && s.activeThread.conversation.id === conversationId
      && binding(s.activeThread.conversation) === klantBinding
      && actieBronId(s.activeThread.messages, s.aiActiesMessageId, s.activeThread.conversation) === messageId
      && actueleKlantbronId(s.activeThread.messages, s.activeThread.conversation) === actueleBronId
      && s.klachtAdvies?.plan?.behandeling_id === behandelingId
      && s.klachtAdvies?.plan?.generatie_id === generatieId;
    set({ klachtBeoordelingBezig: true, isGeneratingKlachtAdvies: true, klachtAdviesError: null, aiDraftError: null });
    try {
      const data = await wijzigKlachtBehandeling({
        id: behandelingId, versie: behandeling.versie, actie: 'beoordeel', beoordeling,
        notitie: beoordeling.toelichting,
        inbox: { conversation_id: conversationId, message_id: messageId },
      });
      const opgeslagen = data.ok && data.behandeling?.id === behandelingId ? data.behandeling : undefined;
      if (!opgeslagen) throw new Error('Het opslaan van de beoordeling is niet bevestigd. Ververs voordat je opnieuw opslaat.');
      if (!isCurrent(get())) return { behandeling: opgeslagen, conceptGemaakt: false };

      const concept = data.concept;
      let fout: string | null = null;
      if (!concept?.ok) {
        const detail = concept?.error?.replace(/^Het oordeel is opgeslagen\.?\s*/i, '').trim();
        fout = `Beoordeling opgeslagen; het nieuwe concept kon niet worden gemaakt.${detail ? ` ${detail}` : ''} Werk het antwoord opnieuw bij; sla de beoordeling niet nogmaals op.`;
      } else if (concept.messageId !== messageId || concept.advies.plan?.behandeling_id !== opgeslagen.id
        || concept.advies.plan?.beoordeling_versie !== opgeslagen.versie) {
        fout = 'Beoordeling opgeslagen; het teruggegeven concept hoort niet bij dit bericht en deze behandelversie. Ververs het gesprek en werk het antwoord opnieuw bij.';
      }
      if (fout || !concept?.ok) {
        const error = fout || 'Beoordeling opgeslagen; werk het antwoord opnieuw bij.';
        set({ aiDraftError: error, klachtAdviesError: error });
        return { behandeling: opgeslagen, error, conceptGemaakt: false };
      }

      const acties = leesActies(concept.acties);
      const timestamp = new Date().toISOString();
      set((s) => {
        if (!isCurrent(s) || !s.activeThread) return {};
        return {
          klachtAdvies: concept.advies, klachtAdviesError: null,
          aiDraft: concept.draft, aiDraftAnalysis: null, aiDraftConfidence: concept.confidence ?? null,
          aiDraftConfidenceReason: concept.confidence_reason || null, aiDraftModel: concept.model || null,
          aiDraftError: null, aiDraftSource: 'on-demand' as const,
          aiActies: acties, aiActieContext: concept.actie_context ?? null, aiActiesMessageId: messageId,
          activeThread: {
            ...s.activeThread,
            messages: s.activeThread.messages.map((m) => m.id === messageId && m.is_incoming ? {
              ...m, ai_draft_text: concept.draft, ai_draft_status: 'generated', ai_draft_generated_at: timestamp,
              ai_draft_model: concept.model, ai_draft_confidence: concept.confidence,
              ai_draft_confidence_reason: concept.confidence_reason,
              ai_klacht_advies: JSON.stringify(concept.advies), ai_klacht_advies_status: 'generated',
              ai_klacht_advies_generated_at: timestamp, ai_acties: acties, ai_actie_context: concept.actie_context ?? null,
            } : m),
          },
        };
      });
      return { behandeling: opgeslagen, conceptGemaakt: true };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Beoordeling opslaan mislukt.';
      if (isCurrent(get())) set({ aiDraftError: message, klachtAdviesError: message });
      return { error: message, conceptGemaakt: false };
    } finally {
      set({ klachtBeoordelingBezig: false, isGeneratingKlachtAdvies: false });
    }
  },

  updateStatus: async (id: string, status: string) => {
    const prev = get().conversations.find(c => c.id === id);
    const prevStatus = prev?.status;

    // Optimistic update BEFORE the API call
    set((state) => {
      // Should this item be removed from the visible list?
      const shouldRemove = !statusPastBijFilter(state.statusFilter, status);
      const updated = shouldRemove
        ? state.conversations.filter((c) => c.id !== id)
        : state.conversations.map((c) =>
            c.id === id ? { ...c, status: status as Conversation['status'] } : c
          );

      // Optimistic count adjustment
      let { nieuwCount, inBehandelingCount, total } = state;
      if (prevStatus === 'nieuw') nieuwCount = Math.max(0, nieuwCount - 1);
      if (prevStatus === 'in_behandeling') inBehandelingCount = Math.max(0, inBehandelingCount - 1);
      if (status === 'nieuw') nieuwCount++;
      if (status === 'in_behandeling') inBehandelingCount++;
      if (shouldRemove) total = Math.max(0, total - 1);

      return {
        conversations: updated,
        nieuwCount,
        inBehandelingCount,
        total,
        activeThread: state.activeThread?.conversation.id === id
          ? {
            ...state.activeThread,
            conversation: { ...state.activeThread.conversation, status: status as Conversation['status'] },
          }
          : state.activeThread,
      };
    });

    try {
      await apiFetch('/api/inbox/status', {
        method: 'PATCH',
        body: JSON.stringify({ conversation_id: id, status }),
      });
    } catch (error) {
      console.error('[Inbox] updateStatus error:', error);
      // Rollback on failure
      if (prev) {
        set((state) => ({
          conversations: state.conversations.some(c => c.id === id)
            ? state.conversations.map(c => c.id === id ? prev : c)
            : [...state.conversations, prev].sort((a, b) =>
                (b.last_message_at || '').localeCompare(a.last_message_at || '')
              ),
        }));
        get().fetchConversations();
      }
    }
  },

  updateSubject: async (id: string, subject: string) => {
    // Optimistic update
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === id ? { ...c, subject } : c
      ),
      activeThread: state.activeThread?.conversation.id === id
        ? {
          ...state.activeThread,
          conversation: { ...state.activeThread.conversation, subject },
        }
        : state.activeThread,
    }));

    try {
      await apiFetch('/api/inbox/status', {
        method: 'PATCH',
        body: JSON.stringify({ conversation_id: id, subject }),
      });
    } catch (error) {
      console.error('[Inbox] updateSubject error:', error);
    }
  },

  snoozeConversation: async (id: string, until: string | null) => {
    const state = get();
    const prevConv = state.conversations.find(c => c.id === id)
      ?? (state.activeThread?.conversation.id === id ? state.activeThread.conversation : undefined);

    // ── Snapshot voor rollback ──
    const snapshot = {
      conversations: state.conversations,
      activeThread: state.activeThread,
      nieuwCount: state.nieuwCount,
      inBehandelingCount: state.inBehandelingCount,
      laterCount: state.laterCount,
      total: state.total,
    };

    // ── Optimistic update ──
    // Snoozen: gesprek verdwijnt uit elke tab behalve 'later'. Unsnoozen:
    // verdwijnt uit 'later' (en komt via fetch terug in zijn status-tab).
    set((s) => {
      const isSnoozing = until !== null;
      const shouldRemove = isSnoozing ? s.statusFilter !== 'later' : s.statusFilter === 'later';
      const updatedConvs = shouldRemove
        ? s.conversations.filter((c) => c.id !== id)
        : s.conversations.map((c) => (c.id === id ? { ...c, snoozed_until: until } : c));

      let { nieuwCount, inBehandelingCount, laterCount, total } = s;
      const status = prevConv?.status;
      const delta = isSnoozing ? -1 : 1;
      if (status === 'nieuw') nieuwCount = Math.max(0, nieuwCount + delta);
      if (status === 'in_behandeling') inBehandelingCount = Math.max(0, inBehandelingCount + delta);
      laterCount = Math.max(0, laterCount - delta);
      if (shouldRemove) total = Math.max(0, total - 1);

      return {
        conversations: updatedConvs,
        nieuwCount,
        inBehandelingCount,
        laterCount,
        total,
        activeThread: s.activeThread?.conversation.id === id
          ? {
              ...s.activeThread,
              conversation: { ...s.activeThread.conversation, snoozed_until: until },
            }
          : s.activeThread,
      };
    });

    try {
      await apiFetch('/api/inbox/snooze', {
        method: 'POST',
        body: JSON.stringify({ conversation_id: id, snoozed_until: until }),
      });
    } catch (error) {
      console.error('[Inbox] snoozeConversation error:', error);
      // ── Rollback ──
      set((s) => ({
        conversations: snapshot.conversations,
        activeThread: s.activeThread?.conversation.id === id ? snapshot.activeThread : s.activeThread,
        nieuwCount: snapshot.nieuwCount,
        inBehandelingCount: snapshot.inBehandelingCount,
        laterCount: snapshot.laterCount,
        total: snapshot.total,
      }));
      get().fetchConversations();
      throw error;
    }
  },

  fetchTeam: async () => {
    if (get().teamMembers.length > 0) return;
    try {
      const data = await apiFetch('/api/users/team');
      set({ teamMembers: (data.members || []) as TeamMember[] });
    } catch (error) {
      console.error('[Inbox] fetchTeam error:', error);
    }
  },

  assignConversation: async (id: string, assigneeEmail: string | null) => {
    const state = get();
    const prevConv = state.conversations.find(c => c.id === id)
      ?? (state.activeThread?.conversation.id === id ? state.activeThread.conversation : undefined);
    const prevStatus = prevConv?.status;
    const prevAssignee = prevConv?.assigned_to?.toLowerCase() ?? null;
    const newAssignee = assigneeEmail ? assigneeEmail.toLowerCase() : null;
    const myEmail = useAuthStore.getState().user?.email?.toLowerCase() ?? null;

    // Status-bump spiegelt het endpoint: toewijzen op 'nieuw' → 'in_behandeling'
    const bumpsStatus = !!newAssignee && prevStatus === 'nieuw';
    const newStatus = bumpsStatus ? ('in_behandeling' as const) : prevStatus;

    // ── Snapshot voor rollback ──
    const snapshot = {
      conversations: state.conversations,
      activeThread: state.activeThread,
      nieuwCount: state.nieuwCount,
      inBehandelingCount: state.inBehandelingCount,
      mijnCount: state.mijnCount,
      total: state.total,
    };

    // ── Optimistic update ──
    set((s) => {
      // Matcht het gesprek nog het actieve filter? 'mijn' kijkt naar eigenaar,
      // status-filters naar (gebumpte) status.
      const leavesFilter =
        s.statusFilter === 'mijn'
          ? newAssignee !== myEmail
          : bumpsStatus && !statusPastBijFilter(s.statusFilter, 'in_behandeling');
      const updatedConvs = leavesFilter
        ? s.conversations.filter((c) => c.id !== id)
        : s.conversations.map((c) =>
            c.id === id
              ? { ...c, assigned_to: newAssignee, ...(newStatus ? { status: newStatus } : {}) }
              : c
          );

      let { nieuwCount, inBehandelingCount, mijnCount, total } = s;
      if (bumpsStatus) {
        nieuwCount = Math.max(0, nieuwCount - 1);
        inBehandelingCount++;
      }
      if (myEmail) {
        const wasMine = prevAssignee === myEmail && (prevStatus === 'nieuw' || prevStatus === 'in_behandeling');
        const isMine = newAssignee === myEmail && (newStatus === 'nieuw' || newStatus === 'in_behandeling');
        if (wasMine && !isMine) mijnCount = Math.max(0, mijnCount - 1);
        if (!wasMine && isMine) mijnCount++;
      }
      if (leavesFilter) total = Math.max(0, total - 1);

      return {
        conversations: updatedConvs,
        nieuwCount,
        inBehandelingCount,
        mijnCount,
        total,
        activeThread: s.activeThread?.conversation.id === id
          ? {
              ...s.activeThread,
              conversation: {
                ...s.activeThread.conversation,
                assigned_to: newAssignee,
                ...(newStatus ? { status: newStatus } : {}),
              },
            }
          : s.activeThread,
      };
    });

    try {
      await apiFetch('/api/inbox/assign', {
        method: 'POST',
        body: JSON.stringify({ conversation_id: id, assignee_email: newAssignee }),
      });
    } catch (error) {
      console.error('[Inbox] assignConversation error:', error);
      // ── Rollback (activeThread alleen als rep nog op dit gesprek zit) ──
      set((s) => ({
        conversations: snapshot.conversations,
        activeThread: s.activeThread?.conversation.id === id ? snapshot.activeThread : s.activeThread,
        nieuwCount: snapshot.nieuwCount,
        inBehandelingCount: snapshot.inBehandelingCount,
        mijnCount: snapshot.mijnCount,
        total: snapshot.total,
      }));
      get().fetchConversations();
      throw error;
    }
  },

  updateLeadField: async (leadId: string, fields: Record<string, string>) => {
    try {
      // Geaudite, Brein-klare write-pad (vervangt de ongeaudite lead-update PATCH).
      const payload: { leadId: string; status?: string; reason?: string } = { leadId };
      if (typeof fields.Status === 'string') payload.status = fields.Status;
      if (typeof fields.Reden_niet_geintereseerd__c === 'string') {
        payload.reason = fields.Reden_niet_geintereseerd__c;
      }
      await apiFetch('/api/offertes/set-status', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      // Optimistic update in leads array
      set((state) => {
        if (!state.activeThread?.customer?.leads) return state;
        return {
          activeThread: {
            ...state.activeThread,
            customer: {
              ...state.activeThread.customer,
              leads: state.activeThread.customer.leads.map(l =>
                l.id === leadId
                  ? {
                      ...l,
                      status: fields.Status ?? l.status,
                      redenNietGeintereseerd: fields.Reden_niet_geintereseerd__c ?? l.redenNietGeintereseerd,
                    }
                  : l
              ),
            },
          },
        };
      });
    } catch (error) {
      console.error('[Inbox] updateLeadField error:', error);
      throw error;
    }
  },

  deleteLead: async (leadId: string) => {
    try {
      await apiFetch(`/api/customers/lead-update?id=${leadId}`, {
        method: 'DELETE',
      });
      // Remove from leads array
      set((state) => {
        if (!state.activeThread?.customer?.leads) return state;
        const updatedLeads = state.activeThread.customer.leads.filter(l => l.id !== leadId);
        return {
          activeThread: {
            ...state.activeThread,
            customer: {
              ...state.activeThread.customer,
              leads: updatedLeads,
            },
          },
        };
      });
    } catch (error) {
      console.error('[Inbox] deleteLead error:', error);
      throw error;
    }
  },

  setBrandFilter: (brand: string) => {
    set({ brandFilter: brand, page: 1 });
    get().fetchConversations();
  },

  setStatusFilter: (status: string) => {
    set({ statusFilter: status, page: 1, searchQuery: '' });
    get().fetchConversations();
  },

  setChannelFilter: (channel: 'all' | 'email' | 'whatsapp' | 'phone') => {
    set({ channelFilter: channel, page: 1 });
    get().fetchConversations();
  },

  setBaanFilter: (baan: string) => {
    set({ baanFilter: baan, page: 1 });
    get().fetchConversations();
  },

  setBaanTabsZichtbaar: (zichtbaar: boolean) => {
    if (get().baanTabsZichtbaar === zichtbaar) return;
    set({ baanTabsZichtbaar: zichtbaar });
    if (zichtbaar) get().fetchConversations();
  },

  setSearchQuery: (query: string) => {
    set({ searchQuery: query, page: 1 });
    get().fetchConversations();
  },

  linkCustomerToConversation: async ({
    conversationId,
    customerId,
    customerType,
    customerName,
    contactId,
    contactName,
    rememberDomain,
    matchSignal,
  }) => {
    const huidig = get().activeThread?.conversation.id === conversationId
      ? get().activeThread?.conversation
      : get().conversations.find((conversation) => conversation.id === conversationId);
    if (!huidig || !Number.isInteger(huidig.customer_link_revision) || huidig.customer_link_revision! < 0) {
      throw new Error('Klantkoppeling mist een actuele revisie. Vernieuw het gesprek en probeer opnieuw.');
    }
    let antwoord: { conversation?: Partial<Conversation> };
    try {
      antwoord = await apiFetch('/api/inbox/link-customer', {
        method: 'PATCH',
        body: JSON.stringify({
          conversation_id: conversationId,
          expected_revision: huidig.customer_link_revision,
          customer_id: customerId,
          customer_type: customerType,
          customer_name: customerName,
          contact_id: contactId ?? null,
          contact_name: contactName ?? null,
          remember_domain: rememberDomain === true,
          match_signal: matchSignal ?? 'manual',
        }),
      });
    } catch (error) {
      if (error instanceof InboxApiFout && error.status === 409) {
        if (get().activeThread?.conversation.id === conversationId) {
          const actueel = await apiFetch(`/api/inbox/thread?id=${conversationId}`);
          set((state) => ({
            conversations: state.conversations.map((conversation) => conversation.id === conversationId
              ? { ...conversation, ...actueel.conversation }
              : conversation),
            activeThread: {
              conversation: actueel.conversation,
              messages: actueel.messages || [],
              attachments: actueel.attachments || [],
              customer: actueel.customer || null,
            },
          }));
        } else {
          await get().fetchConversations();
        }
        throw new Error('Klantkoppeling is intussen gewijzigd. Het gesprek is vernieuwd; controleer de actuele koppeling.');
      }
      throw error;
    }
    const serverConversation = antwoord.conversation;
    if (!serverConversation || !Number.isInteger(serverConversation.customer_link_revision)) {
      throw new Error('Klantkoppeling is opgeslagen zonder geldige revisie; vernieuw het gesprek.');
    }

    // De serverrij bevat ook de nieuwe revision; niet lokaal raden.
    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              ...serverConversation,
            }
          : c
      ),
      activeThread: state.activeThread?.conversation.id === conversationId
        ? {
            ...state.activeThread,
            conversation: {
              ...state.activeThread.conversation,
              ...serverConversation,
            },
          }
        : state.activeThread,
    }));

    // Refetch thread so CustomerMatch payload reflects the new link
    const active = get().activeThread;
    if (active?.conversation.id === conversationId) {
      try {
        const data = await apiFetch(`/api/inbox/thread?id=${conversationId}`);
        set({
          activeThread: {
            conversation: data.conversation,
            messages: data.messages || [],
            attachments: data.attachments || [],
            customer: data.customer || null,
          },
        });
      } catch (err) {
        console.error('[Inbox] refetch after link error:', err);
      }
    }
  },

  unlinkCustomerFromConversation: async (conversationId: string) => {
    const huidig = get().activeThread?.conversation.id === conversationId
      ? get().activeThread?.conversation
      : get().conversations.find((conversation) => conversation.id === conversationId);
    if (!huidig || !Number.isInteger(huidig.customer_link_revision) || huidig.customer_link_revision! < 0) {
      throw new Error('Klantkoppeling mist een actuele revisie. Vernieuw het gesprek en probeer opnieuw.');
    }
    let antwoord: { conversation?: Partial<Conversation> };
    try {
      antwoord = await apiFetch('/api/inbox/link-customer', {
        method: 'PATCH',
        body: JSON.stringify({
          conversation_id: conversationId,
          expected_revision: huidig.customer_link_revision,
          customer_id: null,
        }),
      });
    } catch (error) {
      if (error instanceof InboxApiFout && error.status === 409) {
        if (get().activeThread?.conversation.id === conversationId) {
          const actueel = await apiFetch(`/api/inbox/thread?id=${conversationId}`);
          set((state) => ({
            conversations: state.conversations.map((conversation) => conversation.id === conversationId
              ? { ...conversation, ...actueel.conversation }
              : conversation),
            activeThread: {
              conversation: actueel.conversation,
              messages: actueel.messages || [],
              attachments: actueel.attachments || [],
              customer: actueel.customer || null,
            },
          }));
        } else {
          await get().fetchConversations();
        }
        throw new Error('Klantkoppeling is intussen gewijzigd. Het gesprek is vernieuwd; controleer de actuele koppeling.');
      }
      throw error;
    }
    const serverConversation = antwoord.conversation;
    if (!serverConversation || !Number.isInteger(serverConversation.customer_link_revision)) {
      throw new Error('Klantkoppeling is opgeslagen zonder geldige revisie; vernieuw het gesprek.');
    }

    set((state) => ({
      conversations: state.conversations.map((c) =>
        c.id === conversationId
          ? { ...c, ...serverConversation }
          : c
      ),
      activeThread: state.activeThread?.conversation.id === conversationId
        ? {
            ...state.activeThread,
            conversation: {
              ...state.activeThread.conversation,
              ...serverConversation,
            },
            customer: null,
          }
        : state.activeThread,
    }));
  },

  startPolling: (intervalMs = 15000) => {
    const { pollingIntervalId, fetchConversations } = get();
    if (pollingIntervalId) return;

    fetchConversations(); // Initial fetch
    const id = setInterval(() => fetchConversations(), intervalMs);
    set({ pollingIntervalId: id });
  },

  stopPolling: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId) {
      clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },
}));
